import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import type { AiChatResponse, AiPendingAction, AiInsightsDto, InsightCardDto, AiExtractionResult } from "@arcline/shared";
import { api } from "../lib/api";
import { c, font } from "../lib/theme";

// A record the agent extracted from a dropped document, awaiting "create".
interface ExtractDraft { recordType: "company" | "opportunity"; fields: Record<string, string>; fileName?: string }
interface Msg { role: "user" | "ai"; text: string; loading?: boolean; action?: AiPendingAction; tools?: string[]; via?: string; extract?: ExtractDraft; created?: { view: string; label: string } }
const CHIPS = ["What needs my attention?", "What's the pipeline status?", "How do I connect a data source?", "Something is broken, help me report it"];
const AGENT_LABEL: Record<string, string> = { claude: "Claude", openai: "ChatGPT", grok: "Grok", local: "local engine" };
const readDataUrl = (file: File): Promise<string> =>
  new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result)); r.onerror = () => rej(r.error); r.readAsDataURL(file); });

export function Copilot() {
  const loc = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [thread, setThread] = useState<Msg[]>([]);
  const [draft, setDraft] = useState("");
  const [mode, setMode] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [insights, setInsights] = useState<AiInsightsDto | null>(null);
  const [recordType, setRecordType] = useState<"company" | "opportunity">("company");
  const [dragging, setDragging] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // The fix the prototype lacked: scroll to the newest message whenever the
  // thread changes (the prototype relied on prevState in componentDidUpdate,
  // which its runtime never passed, so the chat rendered below the fold).
  useEffect(() => { const el = bodyRef.current; if (el) el.scrollTop = el.scrollHeight; }, [thread]);
  useEffect(() => { if (open) api.aiStatus().then((r) => setMode(r.agentic ? `LIVE · ${(r.providers ?? []).map((p) => AGENT_LABEL[p] ?? p).join(" → ") || "tool-using agent"}` : "LOCAL SYNTHESIS")).catch(() => setMode("LOCAL SYNTHESIS")); }, [open]);
  // Proactive "connecting-the-dots" insights — refreshed on open and whenever
  // the user changes screen (so the "on this screen" suggestion stays current).
  useEffect(() => { if (open) api.aiInsights(loc.pathname).then(setInsights).catch(() => setInsights(null)); }, [open, loc.pathname]);

  const goto = (view: string): void => { setOpen(false); navigate(view); };

  // While open: focus the composer so you can type immediately, and let Esc
  // close the panel (matching the dialog / help-popover behavior elsewhere).
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Replace the trailing loading bubble with the model's response.
  const settle = (r: AiChatResponse | null): void => {
    setThread((t) => t.map((m) => (m.loading ? { role: "ai", text: r?.text || "Could not reach the Copilot. Try again.", action: r?.pending, tools: r?.toolsUsed, via: r ? AGENT_LABEL[r.backend] ?? r.backend : undefined } : m)));
  };

  const ask = async (q: string): Promise<void> => {
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true);
    setThread((t) => [...t, { role: "user", text: question }, { role: "ai", text: "Working across the live pipeline…", loading: true }]);
    setDraft("");
    try { settle(await api.ai(question)); } catch { settle(null); } finally { setBusy(false); }
  };

  const decide = async (action: AiPendingAction, approve: boolean): Promise<void> => {
    setBusy(true);
    // Drop the confirm card and show a loading bubble for the follow-up.
    setThread((t) => [...t.map((m) => (m.action?.id === action.id ? { ...m, action: undefined } : m)), { role: "ai", text: approve ? "Running the action…" : "Cancelling…", loading: true }]);
    try { settle(await api.aiConfirm(action.id, approve)); } catch { settle(null); } finally { setBusy(false); }
  };

  // Document-drop: extract a Company/Opportunity draft from a dropped/chosen file.
  const extractFile = async (file: File): Promise<void> => {
    if (busy || !file) return;
    setBusy(true);
    setThread((t) => [...t, { role: "user", text: `📄 ${file.name}` }, { role: "ai", text: `Reading the document for a ${recordType}…`, loading: true }]);
    try {
      const isText = file.type.startsWith("text/") || /\.(txt|csv|md|markdown|json)$/i.test(file.name);
      const res: AiExtractionResult = isText
        ? await api.aiExtract({ recordType, text: await file.text(), fileName: file.name })
        : await api.aiExtract({ recordType, dataBase64: await readDataUrl(file), mime: file.type || undefined, fileName: file.name });
      setThread((t) => t.map((m) => (m.loading
        ? { role: "ai", text: res.summary || `Extracted a ${res.recordType} draft — review the fields and create it.`, extract: { recordType: res.recordType, fields: res.fields, fileName: file.name } }
        : m)));
    } catch (e) {
      const msg = (e as Error).message;
      const friendly = /needs_key/i.test(msg)
        ? "I need an AI key in the Vault to read documents: Claude (anthropic_api_key), ChatGPT (openai_api_key) or Grok (xai_api_key). Add one and try again."
        : `Couldn't read that document: ${msg}`;
      setThread((t) => t.map((m) => (m.loading ? { role: "ai", text: friendly } : m)));
    } finally { setBusy(false); }
  };

  const createFromExtract = async (idx: number, d: ExtractDraft): Promise<void> => {
    setBusy(true);
    try {
      const r = d.recordType === "company" ? await api.createCompany(d.fields) : await api.createOpportunity(d.fields);
      const view = d.recordType === "company" ? `/companies?company=${r.id}` : `/opportunities/${r.id}`;
      const label = d.fields.name || d.fields.title || `${d.recordType} #${r.id}`;
      setThread((t) => t.map((m, i) => (i === idx ? { ...m, extract: undefined, created: { view, label } } : m)));
    } catch (e) { setThread((t) => [...t, { role: "ai", text: `Create failed: ${(e as Error).message}` }]); } finally { setBusy(false); }
  };

  const onDropFile = (e: React.DragEvent): void => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) extractFile(f);
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} aria-label="Open ArcLine Copilot" className="arc-fab" style={{ position: "fixed", right: 22, bottom: 22, zIndex: 55, display: "flex", alignItems: "center", gap: 9, background: c.blue, border: "none", borderRadius: 8, padding: "11px 17px", cursor: "pointer", boxShadow: "0 6px 18px rgba(16,36,58,0.28)" }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#8FE3BC" }} />
        <span style={{ font: `700 14px ${font.sans}`, letterSpacing: "0.02em", color: c.onAccent }}>Copilot</span>
      </button>
    );
  }

  return (
    <>
      <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(3,5,11,0.5)", zIndex: 56 }} />
      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 404, maxWidth: "100vw", background: c.panel, borderLeft: `1px solid ${c.cyan}33`, zIndex: 57, display: "flex", flexDirection: "column", boxShadow: "-20px 0 50px rgba(0,0,0,0.6)" }}>
        <div style={{ padding: "15px 18px", borderBottom: `1px solid ${c.line}`, background: c.panel2, display: "flex", alignItems: "center", gap: 11 }}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", background: c.cyan, boxShadow: `0 0 10px ${c.cyan}` }} />
          <div style={{ flex: 1 }}>
            <div style={{ font: `800 18px ${font.display}`, color: c.textHi, lineHeight: 1 }}>ArcLine Copilot</div>
            <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: mode.startsWith("LIVE") ? c.green : c.muted, marginTop: 3 }}>{mode || "…"}</div>
          </div>
          <button onClick={() => setOpen(false)} aria-label="Close Copilot" style={{ fontSize: 20, color: c.muted, cursor: "pointer", background: "none", border: "none", padding: 0, lineHeight: 1 }}>✕</button>
        </div>

        <div ref={bodyRef} onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={onDropFile}
          style={{ flex: 1, overflowY: "auto", padding: "16px 18px", minHeight: 0, position: "relative", outline: dragging ? `2px dashed ${c.cyan}` : "none", outlineOffset: -6 }}>
          {dragging && (
            <div style={{ position: "sticky", top: 0, zIndex: 2, background: "rgba(69,199,242,0.12)", border: `1px solid ${c.cyan}66`, borderRadius: 7, padding: "8px 12px", font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.cyan, textAlign: "center", marginBottom: 10 }}>
              DROP TO EXTRACT A {recordType.toUpperCase()}
            </div>
          )}
          <div style={{ background: "rgba(69,199,242,0.06)", border: `1px solid ${c.cyan}33`, borderRadius: 7, padding: "11px 13px", fontSize: 13.5, color: c.text, lineHeight: 1.55 }}>
            Ask about <b>your data</b> ("what's outstanding for Robin Radar?"), <b>how to use ArcLine</b> ("how do I connect a data source?"), or tell me <b>what's broken</b> and I'll file a help desk ticket. Admins can ask "run a health check" and I'll find and fix feed, report and file problems. State-changing actions need your confirmation and your role's permission (server-enforced; the API key never reaches the browser).
          </div>

          {/* Limited-mode banner — the Copilot only reasons/uses tools with an
              AI key (Claude, ChatGPT or Grok); without one, it returns a canned summary. Make the fix
              obvious and one click away. */}
          {mode && !mode.startsWith("LIVE") && (
            <div style={{ marginTop: 12, background: "rgba(255,180,43,0.09)", border: `1px solid ${c.amber}66`, borderRadius: 8, padding: "12px 13px" }}>
              <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.amber, marginBottom: 6 }}>⚠ LIMITED MODE — NOT CONNECTED</div>
              <div style={{ fontSize: 13.5, color: c.text, lineHeight: 1.55, marginBottom: 9 }}>
                The Copilot can't reason or use tools yet. It needs at least one AI key: <b>Claude</b>, <b>ChatGPT</b> or <b>Grok</b>. Add it in the Vault (scope <code style={{ font: `500 12.5px ${font.mono}`, color: c.muted2 }}>anthropic_api_key</code>, <code style={{ font: `500 12.5px ${font.mono}`, color: c.muted2 }}>openai_api_key</code> or <code style={{ font: `500 12.5px ${font.mono}`, color: c.muted2 }}>xai_api_key</code>) and it switches to the live, tool-using agent. With more than one, the next takes over if one fails. Until then you'll get a basic local summary.
              </div>
              <button onClick={() => goto("/vault")} style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: "#FFFFFF", background: c.amber, border: "none", padding: "7px 13px", borderRadius: 6, cursor: "pointer" }}>Open the Vault →</button>
            </div>
          )}

          {/* Proactive insight cards — synthesized from live data, deep-linking
              into the relevant module. Shown until the user starts a chat. */}
          {thread.length === 0 && insights && insights.insights.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted, marginBottom: 9 }}>Connecting the dots</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {insights.insights.map((it) => <InsightCard key={it.id} card={it} onAct={goto} />)}
              </div>
            </div>
          )}
          {thread.length === 0 && insights?.contextSuggestion && (
            <div style={{ marginTop: 14 }}>
              <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted, marginBottom: 7 }}>On this screen</div>
              <div style={{ background: c.panel2, border: `1px solid ${c.line}`, borderRadius: 7, padding: "10px 12px", fontSize: 13.5, color: c.muted2, lineHeight: 1.55 }}>{insights.contextSuggestion}</div>
            </div>
          )}

          {thread.map((m, i) => (
            <div key={i} style={{ marginTop: 12 }}>
              <div style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
                <div style={{ maxWidth: "88%", padding: "9px 12px", borderRadius: m.role === "user" ? "9px 9px 2px 9px" : "9px 9px 9px 2px", fontSize: 14, lineHeight: 1.5, background: m.role === "user" ? "#D7E2EE" : c.panel2, border: `1px solid ${m.role === "user" ? c.blueHi + "4d" : c.cyan + "33"}`, color: m.loading ? c.muted : c.text, fontStyle: m.loading ? "italic" : "normal" }}>{m.text}</div>
              </div>
              {((m.tools && m.tools.length > 0) || m.via) && (
                <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted, marginTop: 4, marginLeft: 2 }}>
                  {m.via && <>answered by {m.via}</>}{m.via && m.tools && m.tools.length > 0 && " · "}{m.tools && m.tools.length > 0 && <>tools: {Array.from(new Set(m.tools)).join(" · ")}</>}
                </div>
              )}
              {m.action && (
                <div style={{ marginTop: 8, background: "rgba(255,180,43,0.07)", border: `1px solid ${c.amber}55`, borderRadius: 8, padding: "11px 13px" }}>
                  <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.amber, marginBottom: 6 }}>⚿ HUMAN CONFIRMATION REQUIRED</div>
                  <div style={{ fontSize: 14, color: c.textHi, marginBottom: 10 }}>{m.action.label}</div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => decide(m.action!, true)} disabled={busy}
                      style={{ flex: 1, font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: "#FFFFFF", background: c.green, border: "none", padding: "8px", borderRadius: 6, cursor: busy ? "wait" : "pointer" }}>RUN ACTION</button>
                    <button onClick={() => decide(m.action!, false)} disabled={busy}
                      style={{ flex: "none", font: `500 13px ${font.sans}`, color: c.muted2, background: c.navItem, border: `1px solid ${c.line2}`, padding: "8px 14px", borderRadius: 6, cursor: busy ? "wait" : "pointer" }}>DISMISS</button>
                  </div>
                </div>
              )}
              {m.extract && <ExtractCard draft={m.extract} busy={busy} onCreate={() => createFromExtract(i, m.extract!)} />}
              {m.created && (
                <button onClick={() => goto(m.created!.view)} style={{ marginTop: 8, font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.green, background: "rgba(43,214,143,0.08)", border: `1px solid ${c.green}55`, padding: "8px 12px", borderRadius: 7, cursor: "pointer", width: "100%", textAlign: "left" }}>✓ Created {m.created.label} — open the record →</button>
              )}
            </div>
          ))}
        </div>

        <div style={{ padding: "11px 16px 14px", borderTop: `1px solid ${c.line}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 9 }}>
            <span style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted }}>DROP A DOC →</span>
            <div style={{ display: "inline-flex", border: `1px solid ${c.line2}`, borderRadius: 6, overflow: "hidden" }}>
              {(["company", "opportunity"] as const).map((rt) => (
                <button key={rt} onClick={() => setRecordType(rt)} title={`Extract a new ${rt}`} style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", padding: "4px 8px", border: "none", borderLeft: rt === "opportunity" ? `1px solid ${c.line2}` : "none", background: recordType === rt ? c.blue : "transparent", color: recordType === rt ? "#fff" : c.muted, cursor: "pointer" }}>{rt === "company" ? "COMPANY" : "OPPORTUNITY"}</button>
              ))}
            </div>
            <input ref={fileRef} type="file" accept=".pdf,.txt,.csv,.md,.markdown,.json,text/*,application/pdf" onChange={(e) => { const f = e.target.files?.[0]; if (f) extractFile(f); e.target.value = ""; }} style={{ display: "none" }} />
            <button onClick={() => fileRef.current?.click()} disabled={busy} title="Choose a document to extract a record from" style={{ marginLeft: "auto", font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.cyan, background: c.panel2, border: `1px solid ${c.cyan}55`, padding: "4px 10px", borderRadius: 6, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1 }}>＋ EXTRACT</button>
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 10 }}>
            {CHIPS.map((q) => (
              <button key={q} onClick={() => ask(q)} disabled={busy} style={{ font: `500 13px ${font.sans}`, color: c.muted, background: "transparent", border: `1px solid ${c.line2}`, padding: "5px 9px", borderRadius: 5, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.5 : 1 }}>{q}</button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <input ref={inputRef} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(draft); }} placeholder="Ask, or ask me to act…"
              style={{ flex: 1, background: c.soft.tile, border: "none", borderRadius: 8, padding: "10px 12px", color: c.textHi, fontSize: 14, outline: "none" }} />
            <button onClick={() => ask(draft)} disabled={busy || !draft.trim()} style={{ font: `500 13px ${font.mono}`, color: c.onAccent, background: busy || !draft.trim() ? "#D7E2EE" : c.blue, border: "none", padding: "10px 14px", borderRadius: 7, cursor: busy || !draft.trim() ? "not-allowed" : "pointer" }}>SEND</button>
          </div>
        </div>
      </div>
    </>
  );
}

// One proactive insight — a colored tag, a title + detail, and an optional
// deep-link into the module the signal points at.
function InsightCard({ card, onAct }: { card: InsightCardDto; onAct: (view: string) => void }) {
  return (
    <div style={{ background: c.panel2, borderLeft: `2px solid ${card.color}`, border: `1px solid ${c.line}`, borderLeftWidth: 2, borderLeftColor: card.color, borderRadius: 7, padding: "11px 12px" }}>
      <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: card.color, marginBottom: 5 }}>{card.tag}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: c.textHi, marginBottom: 4, lineHeight: 1.35 }}>{card.title}</div>
      <div style={{ fontSize: 13, color: c.muted2, lineHeight: 1.5 }}>{card.detail}</div>
      {card.action && (
        <button onClick={() => onAct(card.action!.view)} style={{ marginTop: 9, font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: card.color, background: "transparent", border: `1px solid ${card.color}55`, padding: "5px 10px", borderRadius: 6, cursor: "pointer" }}>{card.action.label}</button>
      )}
    </div>
  );
}

// A record draft extracted from a dropped document — the fields the agent found,
// plus a one-click create that lands you on the new (editable) record.
function ExtractCard({ draft, busy, onCreate }: { draft: ExtractDraft; busy: boolean; onCreate: () => void }) {
  const entries = Object.entries(draft.fields);
  const canCreate = draft.recordType === "company" ? !!draft.fields.name : !!draft.fields.title;
  return (
    <div style={{ marginTop: 8, background: "rgba(69,199,242,0.06)", border: `1px solid ${c.cyan}44`, borderRadius: 8, padding: "11px 13px" }}>
      <div style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.cyan, marginBottom: 8 }}>EXTRACTED {draft.recordType.toUpperCase()} · REVIEW &amp; CREATE</div>
      {entries.length === 0 ? (
        <div style={{ fontSize: 13, color: c.muted, marginBottom: 9 }}>No fields found — try a richer document, or paste the text.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 10 }}>
          {entries.map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 8, fontSize: 13, lineHeight: 1.45 }}>
              <span style={{ font: `500 13px ${font.sans}`, color: c.muted, minWidth: 86, flex: "none" }}>{k}</span>
              <span style={{ color: c.text, overflow: "hidden", textOverflow: "ellipsis" }}>{v.length > 160 ? `${v.slice(0, 160)}…` : v}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
        <button onClick={onCreate} disabled={busy || !canCreate} style={{ font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: busy || !canCreate ? c.muted : "#FFFFFF", background: busy || !canCreate ? "#D7E2EE" : c.green, border: "none", padding: "8px 14px", borderRadius: 6, cursor: busy || !canCreate ? "not-allowed" : "pointer" }}>
          CREATE {draft.recordType.toUpperCase()}
        </button>
        {!canCreate && entries.length > 0 && <span style={{ fontSize: 12, color: c.amber }}>needs a {draft.recordType === "company" ? "name" : "title"}</span>}
      </div>
    </div>
  );
}
